import * as Leaflet from 'leaflet';
import GroupLogic from '../lib/path-group';
import memoizeOne from 'memoize-one';
const _applyStyle = (ctx, ...styles) => styles.forEach((style) => Object.assign(ctx, style));
const NORMAL_COLOR = '#6099bc';
const HIGHLIGHT_COLOR = '#c4443b';
const GREY_GRAPH_ZONE = '#868686';
function _everyNPixels(localOffset, pix, start, length, dist, callback) {
    //
    // callback(x, y, angle, step_no, is_step)
    //
    // Callback will be called on every path point and also on every
    // (dist) pixels of the path
    //
    // x, y = coordinates
    // step_no = how many steps of (dist) pixel length was passed before this point
    //
    // If callback is called because of achievement of (dist) pixels of the path:
    //     is_step = true
    //     angle = geometric angle of the path in this point
    //
    // If callback is called on path point
    //     is_step = false
    //     angle = null
    //
    let i, dx, dy;
    const l = [];
    for (i = start; i < start + length - 1; i++) {
        dx = pix[i + 1].x - pix[i].x;
        dy = pix[i + 1].y - pix[i].y;
        l.push(Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2)));
    }
    i = 0;
    let dist_till_next_mark = 0;
    let step_no = 0;
    callback(pix[start].x, pix[start].y, null, 0, false);
    // eslint-disable-next-line no-constant-condition
    while (true) {
        dist_till_next_mark += dist;
        while (i < l.length && l[i] < dist_till_next_mark) {
            dist_till_next_mark -= l[i];
            i++;
            callback(pix[start + i].x, pix[start + i].y, null, step_no, false);
        }
        if (i >= l.length) {
            break;
        }
        const p1 = pix[start + i];
        const p2 = pix[start + i + 1];
        dx = p2.x - p1.x;
        dy = p2.y - p1.y;
        const angle = Math.atan2(dy, dx);
        const ax = p1.x + (dist_till_next_mark / l[i]) * dx;
        const ay = p1.y + (dist_till_next_mark / l[i]) * dy;
        // if 0 < ax + localOffset.x < @canvas.width and
        //     0 < ay + localOffset.y < @canvas.height
        callback(ax, ay, angle, step_no, true);
        step_no++;
    }
}
const normal_gps_style = (group) => ({
    outline_style: {
        lineWidth: group.highlight ? 8 : 7,
        strokeStyle: '#ffffff',
        lineJoin: 'round',
    },
    main_style: {
        lineWidth: group.highlight ? 4 : 3.5,
        strokeStyle: group.highlight ? group.colour : GREY_GRAPH_ZONE,
        lineJoin: 'round',
    },
    mark_style: {
        lineWidth: 2,
        strokeStyle: '#ffffff',
        lineJoin: 'miter',
    },
    mark_size: group.highlight ? { x: 6, y: 6 } : { x: 5, y: 5 },
});
const single_gps_style = (highlight) => ({
    outline_style: {
        lineWidth: 6,
        strokeStyle: '#ffffff',
    },
    main_style: {
        lineWidth: 3,
        strokeStyle: highlight ? HIGHLIGHT_COLOR : NORMAL_COLOR,
    },
    arrow_outline_style: {
        lineWidth: 3,
        strokeStyle: '#ffffff',
        fillStyle: '#ffffff',
    },
    arrow_style: {
        lineWidth: 1,
        fillStyle: highlight ? HIGHLIGHT_COLOR : NORMAL_COLOR,
        x: 6,
        y: 10,
    },
    points: {
        radius: 5,
        style: {
            fillStyle: HIGHLIGHT_COLOR,
            strokeStyle: '#ffffff',
            lineWidth: 2,
        },
    },
});
function get_style_for_gps_group(group) {
    const highlight = group.highlight;
    return {
        outline_style: {
            lineWidth: highlight ? 8 : 7,
            strokeStyle: '#ffffff',
            lineJoin: 'round',
        },
        main_style: {
            lineWidth: highlight ? 4 : 3.5,
            strokeStyle: group.colour,
            lineJoin: 'round',
        },
        mark_style: {
            lineWidth: 2,
            strokeStyle: '#ffffff',
            lineJoin: 'miter',
        },
        mark_size: highlight ? { x: 6, y: 6 } : { x: 5, y: 5 },
    };
}
class PathRenderer extends Leaflet.Canvas {
    styles;
    _drawing;
    _ctx;
    constructor(options) {
        super({
            ...options,
            padding: 0.5,
        });
        this.styles = {
            single_gps: single_gps_style(false),
            single_gps_highlight: single_gps_style(true),
            teleport: {
                outline_style: {
                    lineWidth: 6,
                    strokeStyle: '#ffffff',
                },
                main_style: {
                    lineWidth: 3,
                    strokeStyle: '#808080',
                },
                arrow_outline_style: {
                    lineWidth: 3,
                    strokeStyle: '#ffffff',
                    fillStyle: '#ffffff',
                },
                arrow_style: {
                    lineWidth: 1,
                    fillStyle: '#808080',
                    x: 6,
                    y: 10,
                },
                points: {
                    radius: 5,
                    style: {
                        fillStyle: '#808080',
                        strokeStyle: '#ffffff',
                        lineWidth: 2,
                    },
                },
            },
            single_coarse: {
                outline_style: {
                    lineWidth: 6,
                    strokeStyle: '#ffffff',
                },
                main_style: {
                    lineWidth: 3,
                    strokeStyle: '#2ababe',
                },
                arrow_outline_style: {
                    lineWidth: 3,
                    strokeStyle: '#ffffff',
                    fillStyle: '#ffffff',
                },
                arrow_style: {
                    lineWidth: 1,
                    fillStyle: '#2ababe',
                    x: 6,
                    y: 10,
                },
                points: {
                    radius: 5,
                    style: {
                        fillStyle: '#2ababe',
                        strokeStyle: '#ffffff',
                        lineWidth: 2,
                    },
                },
            },
        };
    }
    componentWillUnmount() {
        this.remove();
    }
    _draw_oriented_line(ctx, xy, { main_style, outline_style, mark_style, mark_size, }) {
        const draw_outline_circle = ({ x, y }) => {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x, y + 1e-2);
            _applyStyle(ctx, outline_style);
            ctx.lineCap = 'round';
            ctx.stroke();
        };
        const first_pnt = xy[0];
        if (first_pnt != null) {
            draw_outline_circle(first_pnt);
        }
        const last_pnt = xy[xy.length - 1];
        if (last_pnt != null) {
            draw_outline_circle(last_pnt);
        }
        let segment_points = [];
        let segment_arrows = [];
        const draw_segment = () => {
            let x, y;
            ctx.beginPath();
            for (let i = 0; i < segment_points.length; i++) {
                ({ x, y } = segment_points[i]);
                ctx[i ? 'lineTo' : 'moveTo'](x, y);
            }
            _applyStyle(ctx, outline_style);
            ctx.lineCap = 'butt';
            ctx.stroke();
            _applyStyle(ctx, main_style);
            ctx.lineCap = 'butt';
            ctx.stroke();
            if (segment_arrows.length > 0) {
                let angle;
                if (mark_style)
                    _applyStyle(ctx, mark_style);
                if (mark_size)
                    for ([x, y, angle] of segment_arrows) {
                        ctx.save();
                        ctx.translate(x, y);
                        ctx.rotate(angle);
                        ctx.beginPath();
                        ctx.moveTo(-mark_size.y, -mark_size.x / 2);
                        ctx.lineTo(0, 0);
                        ctx.lineTo(-mark_size.y, mark_size.x / 2);
                        ctx.stroke();
                        ctx.restore();
                    }
            }
            segment_points = [];
            segment_arrows = [];
        };
        let prev_x = xy[0].x;
        let prev_dx = 0;
        _everyNPixels(null, xy, 0, xy.length, 40, (x, y, angle, step_no, is_step) => {
            if (prev_dx * (x - prev_x) < 0 && segment_points.length > 0) {
                segment_points.push({ x, y });
                draw_segment();
            }
            segment_points.push({ x, y });
            prev_dx = x - prev_x;
            prev_x = x;
            if (is_step && angle) {
                segment_arrows.push([x, y, angle]);
            }
        });
        if (segment_points.length > 0) {
            draw_segment();
        }
    }
    _draw_arrows(ctx, xy, style) {
        const arrow_dx = style.arrow_style && style.arrow_style.x / 2;
        const arrow_dy = style.arrow_style && style.arrow_style.y / 2;
        ctx.beginPath();
        _everyNPixels(null, xy, 0, xy.length, 15, (x, y, angle, step_no, is_step) => {
            if (step_no % 3 === 0) {
                ctx.lineTo(x, y);
                if (is_step) {
                    _applyStyle(ctx, style.outline_style, {
                        lineCap: 'square',
                    });
                    ctx.stroke();
                    _applyStyle(ctx, style.main_style, { lineCap: 'square' });
                    ctx.stroke();
                    ctx.save();
                    ctx.translate(x, y);
                    if (typeof angle === 'number') {
                        ctx.rotate(angle);
                    }
                    ctx.beginPath();
                    if (arrow_dx && arrow_dy) {
                        ctx.moveTo(-arrow_dy, -arrow_dx);
                        ctx.lineTo(arrow_dy, 0);
                        ctx.lineTo(-arrow_dy, +arrow_dx);
                    }
                    if (style.arrow_outline_style)
                        _applyStyle(ctx, style.arrow_outline_style, {
                            lineCap: 'round',
                        });
                    ctx.stroke();
                    if (style.arrow_style)
                        _applyStyle(ctx, style.arrow_style, { lineCap: 'round' });
                    ctx.fill();
                    ctx.restore();
                }
            }
            else {
                if (is_step) {
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                }
            }
        });
        if (style.points) {
            _applyStyle(ctx, style.points.style, { strokeStyle: '#3bc499' });
            for (const { x, y } of xy) {
                ctx.beginPath();
                ctx.arc(x, y, style.points.radius, 0, 2 * Math.PI);
                ctx.fill();
                ctx.stroke();
            }
        }
    }
    _updatePoly(layer, closed) {
        if (!this._drawing) {
            return;
        }
        const ctx = this._ctx;
        if (!layer._parts.length) {
            return;
        }
        for (let i = 0; i < layer._parts.length; i++) {
            const xy = layer._parts[i];
            const group = layer.groups[layer._group_numbers[i]];
            switch (group.type) {
                case 'gps':
                    if (group.highlight) {
                        this._draw_oriented_line(ctx, xy, normal_gps_style(group));
                    }
                    else {
                        this._draw_oriented_line(ctx, xy, normal_gps_style(group));
                    }
                    break;
                case 'single_gps':
                    if (group.highlight) {
                        this._draw_arrows(ctx, xy, this.styles.single_gps_highlight);
                    }
                    else {
                        this._draw_arrows(ctx, xy, this.styles.single_gps);
                    }
                    break;
                case 'teleport':
                    this._draw_arrows(ctx, xy, this.styles.teleport);
                    break;
                case 'single_coarse':
                    this._draw_arrows(ctx, xy, this.styles.single_coarse);
                    break;
            }
        }
    }
}
// need for fix many canvas when change period
// but for dashboard needs many maps
const getRenderer = memoizeOne((device_id) => new PathRenderer(), (prev_device_id, new_device_id) => {
    return prev_device_id[0] == new_device_id[0];
});
export class PathPolyline extends Leaflet.Polyline {
    _renderer;
    _parts;
    _group_numbers;
    _pxBounds;
    _rings;
    points;
    events;
    highlightTime;
    groups;
    constructor(latlngs, options, device_id) {
        super(latlngs, {
            ...options,
            weight: 8,
            renderer: getRenderer(device_id),
        });
    }
    calc_groups(points, events, highlightTime) {
        const groupLogic = new GroupLogic();
        return groupLogic.calc_groups(points, events, highlightTime);
    }
    _clipPoints() {
        // ILYA: This is a copy of base method with saving original ring
        //       numbers in @_group_numbers.  It is used to determine
        //       correct drawing style for each part.
        const bounds = this._renderer._bounds;
        this._parts = [];
        this._group_numbers = [];
        if (this._pxBounds == null || !this._pxBounds.intersects(bounds)) {
            return;
        }
        if (this.options.noClip) {
            this._parts = this._rings;
            return;
        }
        const parts = this._parts;
        let k = 0;
        for (let group_no = 0; group_no < this._rings.length; group_no++) {
            const ring = this._rings[group_no];
            const len2 = ring.length;
            for (let j = 0; j < ring.length - 1; j++) {
                const segment = Leaflet.LineUtil.clipSegment(ring[j], ring[j + 1], bounds, j != 0, true);
                if (!segment) {
                    continue;
                }
                if (parts[k] == null) {
                    parts[k] = [];
                }
                this._group_numbers[k] = group_no;
                parts[k].push(segment[0]);
                // if segment goes out of screen, or it's the last one, it's the end of the line part
                if (segment[1] !== ring[j + 1] || j === len2 - 2) {
                    parts[k].push(segment[1]);
                    k++;
                }
            }
        }
    }
    setPoints(points, events, highlightTime) {
        this.points = points;
        this.events = events;
        this.highlightTime = highlightTime;
        this.groups = this.calc_groups(this.points, this.events, this.highlightTime);
        this.setLatLngs(this.groups.map((group) => {
            const results = [];
            for (let i = group.start; i <= group.end; i++) {
                if (this.points[i] == null) {
                    continue;
                }
                results.push([this.points[i].loc[1], this.points[i].loc[0]]);
            }
            return results;
        }));
    }
    find_points_near(layer_xy, hot_radius = 5) {
        const hot_sq = Math.pow(hot_radius, 2);
        const sq_dist = (a, b) => Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2);
        const result = [];
        let nearest_distance_in_series = null;
        let nearest_point_in_series = null;
        for (let ring_no = 0; ring_no < this._rings.length; ring_no++) {
            const ring = this._rings[ring_no];
            for (let i = 0; i < ring.length - 1; i++) {
                const p1 = ring[i];
                const p2 = ring[i + 1];
                // @ts-ignore
                const seg_dist_sq = Leaflet.LineUtil._sqClosestPointOnSegment(layer_xy, p1, p2, true);
                if (seg_dist_sq <= hot_sq) {
                    const dp1_sq = sq_dist(layer_xy, p1);
                    const dp2_sq = sq_dist(layer_xy, p2);
                    const d_sq = dp1_sq < dp2_sq ? dp1_sq : dp2_sq;
                    const index = dp1_sq < dp2_sq ? i : i + 1;
                    if (nearest_distance_in_series === null ||
                        d_sq < nearest_distance_in_series) {
                        nearest_point_in_series =
                            this.points[this.groups[ring_no].start + index];
                    }
                }
                else {
                    if (nearest_point_in_series != null) {
                        result.push(nearest_point_in_series);
                    }
                    nearest_distance_in_series = nearest_point_in_series = null;
                }
            }
        }
        if (nearest_point_in_series != null) {
            result.push(nearest_point_in_series);
        }
        return result;
    }
}
